import { FREQUENCY_TYPES } from './frequencyTypes';

/**
 * Frequency group identifiers.
 *
 * Each income/deduction source in INCOME_SOURCES / DEDUCTIONS_SOURCES references one group.
 * Groups define which frequencies are eligible for that category before state rules are applied.
 * State-specific show/hide rules live in frequencyOptionsConfig.js.
 */
export const FREQUENCY_GROUP = {
    NONE: 'none',
    /** Job, AI/AN */
    JOB: 'job',
    /** Alimony, farming/fishing, other, pension, retirement, rental/royalty, scholarship, unemployment */
    STANDARD: 'standard',
    /** Investment, social security / disability */
    INVESTMENT: 'investment',
    /** Self employment */
    SELF_EMPLOYMENT: 'selfEmployment',
    /** Capital gain */
    CAPITAL_GAIN: 'capitalGain',
    /** All deduction types */
    DEDUCTION: 'deduction',
    /** MN paid family leave (Other income subtype) */
    PAID_FAMILY_LEAVE: 'paidFamilyLeave',
};

/** Prepends the dropdown placeholder option ('default' → "Frequency") to each group's frequency list. */
const withDefault = (...frequencies) => ['default', ...frequencies];

/** Base frequency whitelists per group, shared by all states. State overrides are in frequencyOptionsConfig.js. */
export const FREQUENCY_GROUPS = {
    [FREQUENCY_GROUP.NONE]: [],
    [FREQUENCY_GROUP.JOB]: withDefault(
        FREQUENCY_TYPES.HOURLY,
        FREQUENCY_TYPES.DAILY,
        FREQUENCY_TYPES.WEEKLY,
        FREQUENCY_TYPES.BIWEEKLY,
        FREQUENCY_TYPES.BIMONTHLY,
        FREQUENCY_TYPES.MONTHLY,
        FREQUENCY_TYPES.YEARLY,
        FREQUENCY_TYPES.ONCE,
        FREQUENCY_TYPES.SEASONAL,
        FREQUENCY_TYPES.TWICE_YEARLY,
    ),
    [FREQUENCY_GROUP.STANDARD]: withDefault(
        FREQUENCY_TYPES.WEEKLY,
        FREQUENCY_TYPES.BIWEEKLY,
        FREQUENCY_TYPES.BIMONTHLY,
        FREQUENCY_TYPES.MONTHLY,
        FREQUENCY_TYPES.YEARLY,
        FREQUENCY_TYPES.ONCE,
        FREQUENCY_TYPES.SEASONAL,
        FREQUENCY_TYPES.TWICE_YEARLY,
    ),
    [FREQUENCY_GROUP.INVESTMENT]: withDefault(
        FREQUENCY_TYPES.MONTHLY,
        FREQUENCY_TYPES.QUARTERLY,
        FREQUENCY_TYPES.YEARLY,
        FREQUENCY_TYPES.ONCE,
        FREQUENCY_TYPES.SEASONAL,
        FREQUENCY_TYPES.TWICE_YEARLY,
    ),
    [FREQUENCY_GROUP.SELF_EMPLOYMENT]: withDefault(
        FREQUENCY_TYPES.MONTHLY,
        FREQUENCY_TYPES.YEARLY,
        FREQUENCY_TYPES.SEASONAL,
        FREQUENCY_TYPES.TWICE_YEARLY,
    ),
    [FREQUENCY_GROUP.CAPITAL_GAIN]: withDefault(
        FREQUENCY_TYPES.YEARLY,
        FREQUENCY_TYPES.SEASONAL,
        FREQUENCY_TYPES.TWICE_YEARLY,
    ),
    [FREQUENCY_GROUP.DEDUCTION]: withDefault(
        FREQUENCY_TYPES.WEEKLY,
        FREQUENCY_TYPES.BIWEEKLY,
        FREQUENCY_TYPES.BIMONTHLY,
        FREQUENCY_TYPES.MONTHLY,
        FREQUENCY_TYPES.YEARLY,
        FREQUENCY_TYPES.SEASONAL,
        FREQUENCY_TYPES.TWICE_YEARLY,
    ),
    [FREQUENCY_GROUP.PAID_FAMILY_LEAVE]: withDefault(
        FREQUENCY_TYPES.WEEKLY,
        FREQUENCY_TYPES.BIWEEKLY,
        FREQUENCY_TYPES.BIMONTHLY,
        FREQUENCY_TYPES.MONTHLY,
        FREQUENCY_TYPES.QUARTERLY,
        FREQUENCY_TYPES.TWICE_YEARLY,
        FREQUENCY_TYPES.YEARLY,
        FREQUENCY_TYPES.ONCE,
    ),
};

/** Resolves the frequency group for a source, applying frequencyGroupBySubtype when present. */
export const resolveFrequencyGroupForSource = (source, subtype) => {
    if (!source?.frequencyGroup) {
        return FREQUENCY_GROUP.NONE;
    }

    if (subtype && source.frequencyGroupBySubtype?.[subtype]) {
        return source.frequencyGroupBySubtype[subtype];
    }

    return source.frequencyGroup;
};
